/**
 * Derive a confidence label (Strong / Standard / Watch) for an upcoming pick
 * based on the simulator's `reason` text. The simulator already encodes the
 * trigger ordinal (e.g. "3rd W1-after-L of cycle…") so we can read that
 * directly without re-running the engine on the client.
 *
 * Heuristic:
 *   Charlie (iso-1L 2nd/3rd/5th/7th/8th in cycle):
 *     - 5th, 7th, 8th  → Strong (deep cycle, edge well-established)
 *     - 2nd, 3rd       → Standard
 *     - other          → Watch
 *
 *   Dakota (Nth W1-after-L of cycle):
 *     - 3rd, 4th       → Strong (proven bounce pattern)
 *     - 1st, 2nd       → Standard
 *     - 5th+           → Watch (cycle may be over-extended)
 */
const ORDINAL_TO_N = {
  "1st": 1, "2nd": 2, "3rd": 3, "4th": 4, "5th": 5,
  "6th": 6, "7th": 7, "8th": 8, "9th": 9,
};

export function deriveConfidence(pick) {
  if (!pick) return null;
  const reason = pick.reason || pick.recommendation || "";
  const analyst = pick.analyst || (pick.bet_type === "wc" ? "Dakota" : "Charlie");

  // Extract the leading ordinal from the reason text.
  let n = null;
  const m = reason.match(/^(\d+(?:st|nd|rd|th)|\d+)/i);
  if (m) {
    const tok = m[1].toLowerCase();
    n = ORDINAL_TO_N[tok] != null ? ORDINAL_TO_N[tok] : Number(tok);
  }

  if (n == null) return { label: "Standard", level: "standard", color: "#71717A" };

  if (analyst === "Charlie") {
    if ([5, 7, 8].includes(n)) return { label: "Strong", level: "strong", color: "#22C55E", reason: `${n}th iso-1L deep in cycle` };
    if ([2, 3].includes(n)) return { label: "Standard", level: "standard", color: "#007AFF", reason: `${n}th iso-1L` };
    return { label: "Watch", level: "watch", color: "#F59E0B", reason: `${n}th iso-1L · light cycle data` };
  }
  // Dakota
  if ([3, 4].includes(n)) return { label: "Strong", level: "strong", color: "#22C55E", reason: `${n}th W1-after-L · proven bounce` };
  if ([1, 2].includes(n)) return { label: "Standard", level: "standard", color: "#A855F7", reason: `${n}th W1-after-L` };
  return { label: "Watch", level: "watch", color: "#F59E0B", reason: `${n}th W1-after-L · cycle over-extended` };
}
